import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useColorTheme } from '../../context/ColorThemeContext';
import PosterForm from './PosterForm';
import PosterCanvas from './PosterCanvas';
import ExportControls from './ExportControls';
import {
  ArrowLeft, ZoomIn, ZoomOut, Maximize2, Save, FolderOpen, Trash2, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMAT_OPTIONS = [
  { id: 'square', label: 'Square', w: 1080, h: 1080 },
  { id: 'portrait', label: 'Portrait', w: 1080, h: 1350 },
  { id: 'story', label: 'Story', w: 1080, h: 1920 },
  { id: 'a4', label: 'A4', w: 2480, h: 3508 },
];

const INITIAL_FORM = {
  org_name: '',
  org_name_secondary: '',
  logo_url: null,
  event_title: '',
  event_subtitle: '',
  date: '',
  time: '',
  schedule: [],
  venue_name: '',
  venue_address: '',
  contact_info: '',
  footer_message: '',
  theme: 'temple_gold',
  format: 'square',
  ai_background_url: null,
  bg_color: null,
};

export default function PosterBuilder({ onBack, sessionToken }) {
  const { colorTheme } = useColorTheme();
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [freeFormElements, setFreeFormElements] = useState([]);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [zoom, setZoom] = useState(0.55);
  const [isGeneratingBg, setIsGeneratingBg] = useState(false);
  // Save/load state
  const [currentPosterId, setCurrentPosterId] = useState(null);
  const [posterName, setPosterName] = useState('');
  const [savedPosters, setSavedPosters] = useState([]);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const canvasRef = useRef(null);

  const gradient = colorTheme === 'purple' ? 'from-purple-600 to-pink-600'
    : colorTheme === 'blue' ? 'from-blue-600 to-sky-500'
    : 'from-emerald-600 to-teal-500';

  const updateField = useCallback((field, value) => {
    // When the format (canvas aspect ratio) changes, proportionally rescale
    // every free-form element so it stays in the same RELATIVE position and
    // occupies the same fraction of the canvas. Otherwise, an element placed
    // at (440px, 900px) in a square canvas would end up wildly off in A4
    // (2480×3508) — the bug the user reported with the overlapping QR code.
    if (field === 'format' && value && value !== formData.format) {
      const oldFmt = FORMAT_OPTIONS.find(f => f.id === formData.format);
      const newFmt = FORMAT_OPTIONS.find(f => f.id === value);
      if (oldFmt && newFmt) {
        const xRatio = newFmt.w / oldFmt.w;
        const yRatio = newFmt.h / oldFmt.h;
        setFreeFormElements(prev => prev.map(el => ({
          ...el,
          x: el.x * xRatio,
          y: el.y * yRatio,
          width: el.width * xRatio,
          height: el.height * yRatio,
        })));
      }
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  }, [formData.format]);

  const [bgError, setBgError] = useState('');

  const handleGenerateBackground = useCallback(async (category, customPrompt) => {
    setIsGeneratingBg(true);
    setBgError('');
    try {
      // Phase 1: submit the task (returns within 5-20s)
      const startResp = await fetch(`${API}/poster-builder/generate-background`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category,
          custom_prompt: customPrompt || null,
          theme: formData.theme,
          format: formData.format,
        }),
      });
      let startData;
      try {
        startData = await startResp.json();
      } catch {
        throw new Error('Service is busy. Please try again in a moment.');
      }
      if (!startResp.ok || !startData.task_id) {
        throw new Error(startData?.detail || `Failed to start (${startResp.status})`);
      }

      // Phase 2: poll for completion (max 4 minutes)
      const taskId = startData.task_id;
      const POLL_INTERVAL_MS = 5000;
      const MAX_POLLS = 48;
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
        const statusResp = await fetch(`${API}/poster-builder/background-status/${taskId}`);
        let statusData;
        try {
          statusData = await statusResp.json();
        } catch {
          // Transient proxy/JSON error — keep polling
          continue;
        }
        if (statusData.state === 'completed' && statusData.image_url) {
          updateField('ai_background_url', statusData.image_url);
          return;
        }
        if (statusData.state === 'failed') {
          throw new Error(statusData.error || 'Background generation failed.');
        }
      }
      throw new Error('Background generation timed out. Please retry.');
    } catch (err) {
      console.error('Background generation failed:', err);
      setBgError(err?.message || 'Network error while generating background. Please retry.');
    } finally {
      setIsGeneratingBg(false);
    }
  }, [formData.theme, formData.format, updateField]);

  const addFreeFormImage = useCallback(async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const resp = await fetch(`${API}/poster-builder/upload-image`, {
        method: 'POST',
        body: fd,
      });
      const data = await resp.json();
      if (data.url) {
        const newEl = {
          id: `img-${Date.now()}`,
          type: 'image',
          x: 50,
          y: 50,
          width: 200,
          height: 200,
          url: data.url,
          rotation: 0,
          opacity: 1,
          zIndex: 10 + freeFormElements.length,
        };
        setFreeFormElements(prev => [...prev, newEl]);
        setSelectedElementId(newEl.id);
      }
    } catch (err) {
      console.error('Image upload failed:', err);
    }
  }, [freeFormElements.length]);

  const addFreeFormText = useCallback(() => {
    const newEl = {
      id: `txt-${Date.now()}`,
      type: 'text',
      x: 50,
      y: 50,
      width: 300,
      height: 60,
      content: 'Double-click to edit',
      fontSize: 28,
      fontColor: '#FFFFFF',
      fontFamily: 'sans-serif',
      rotation: 0,
      opacity: 1,
      zIndex: 10 + freeFormElements.length,
    };
    setFreeFormElements(prev => [...prev, newEl]);
    setSelectedElementId(newEl.id);
  }, [freeFormElements.length]);

  const updateElement = useCallback((id, updates) => {
    setFreeFormElements(prev =>
      prev.map(el => (el.id === id ? { ...el, ...updates } : el))
    );
  }, []);

  const removeElement = useCallback((id) => {
    setFreeFormElements(prev => prev.filter(el => el.id !== id));
    if (selectedElementId === id) setSelectedElementId(null);
  }, [selectedElementId]);

  const currentFormat = FORMAT_OPTIONS.find(f => f.id === formData.format) || FORMAT_OPTIONS[0];

  // ─────────────────────────────────────────────────────
  // Save / Load handlers
  // ─────────────────────────────────────────────────────
  const fetchSavedPosters = useCallback(async () => {
    if (!sessionToken) return;
    try {
      const resp = await fetch(`${API}/poster-builder/list`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setSavedPosters(data.posters || []);
      }
    } catch (err) {
      console.error('Failed to fetch saved posters:', err);
    }
  }, [sessionToken]);

  useEffect(() => { fetchSavedPosters(); }, [fetchSavedPosters]);

  const captureThumbnail = useCallback(async () => {
    if (!canvasRef.current) return null;
    try {
      const { default: html2canvas } = await import('html2canvas');
      const fmt = FORMAT_OPTIONS.find(f => f.id === formData.format) || FORMAT_OPTIONS[0];
      // Render the thumbnail at a fixed width of 600px (preserves aspect
      // ratio) so it stays sharp even for A4 (2480px wide) source canvases.
      const THUMB_TARGET_WIDTH = 600;
      const scale = THUMB_TARGET_WIDTH / fmt.w;
      const canvas = await html2canvas(canvasRef.current, {
        scale,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: fmt.w,
        height: fmt.h,
        windowWidth: fmt.w,
        windowHeight: fmt.h,
      });
      // High-quality JPEG keeps file size small while preserving legibility.
      return canvas.toDataURL('image/jpeg', 0.88);
    } catch (err) {
      console.warn('Thumbnail capture failed:', err);
      return null;
    }
  }, [formData.format]);

  const handleSavePoster = useCallback(async () => {
    if (!sessionToken) {
      setSaveError('Please sign in to save posters');
      return;
    }
    setIsSaving(true);
    setSaveError('');
    try {
      const thumbnail = await captureThumbnail();
      const resp = await fetch(`${API}/poster-builder/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({
          name: posterName || `Poster ${new Date().toLocaleString()}`,
          form_data: formData,
          free_form_elements: freeFormElements,
          thumbnail_data_url: thumbnail,
          poster_id: currentPosterId,  // null → create, else update
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setSaveError(data.detail || `Save failed (${resp.status})`);
        return;
      }
      setCurrentPosterId(data.id);
      setShowSaveModal(false);
      await fetchSavedPosters();
    } catch (err) {
      console.error('Save poster failed:', err);
      setSaveError(err?.message || 'Save failed');
    } finally {
      setIsSaving(false);
    }
  }, [sessionToken, posterName, formData, freeFormElements, currentPosterId, captureThumbnail, fetchSavedPosters]);

  const handleLoadPoster = useCallback(async (posterId) => {
    try {
      const resp = await fetch(`${API}/poster-builder/load/${posterId}`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Load failed');
      setFormData({ ...INITIAL_FORM, ...(data.form_data || {}) });
      setFreeFormElements(data.free_form_elements || []);
      setCurrentPosterId(data.id);
      setPosterName(data.name || '');
      setShowLoadModal(false);
    } catch (err) {
      console.error('Load poster failed:', err);
      alert(err?.message || 'Failed to load poster');
    }
  }, [sessionToken]);

  const handleDeletePoster = useCallback(async (posterId) => {
    if (!window.confirm('Delete this saved poster? This cannot be undone.')) return;
    try {
      await fetch(`${API}/poster-builder/delete/${posterId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (currentPosterId === posterId) {
        setCurrentPosterId(null);
        setPosterName('');
      }
      await fetchSavedPosters();
    } catch (err) {
      console.error('Delete poster failed:', err);
    }
  }, [sessionToken, currentPosterId, fetchSavedPosters]);

  const handleNewPoster = useCallback(() => {
    if (currentPosterId && !window.confirm('Discard current changes and start a new poster?')) return;
    setFormData(INITIAL_FORM);
    setFreeFormElements([]);
    setCurrentPosterId(null);
    setPosterName('');
  }, [currentPosterId]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="poster-builder-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-800/50 px-4 py-3">
        <div className="flex items-center justify-between max-w-[1800px] mx-auto">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 transition-colors"
              data-testid="poster-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className={`text-xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>
                Event Poster Builder
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">Create pixel-perfect event posters</p>
            </div>
          </div>

          {/* Format selector */}
          <div className="flex items-center gap-2" data-testid="format-selector">
            {FORMAT_OPTIONS.map(f => (
              <button
                key={f.id}
                onClick={() => updateField('format', f.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  formData.format === f.id
                    ? `bg-gradient-to-r ${gradient} text-white shadow-md`
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
                data-testid={`format-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Save / Load / New */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setShowSaveModal(true)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r ${gradient} text-white hover:opacity-90`}
              data-testid="save-poster-btn"
              title="Save this poster"
            >
              <Save className="w-3.5 h-3.5" /> Save
            </button>
            <button
              onClick={() => setShowLoadModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
              data-testid="my-posters-btn"
              title="My saved posters"
            >
              <FolderOpen className="w-3.5 h-3.5" /> My Posters
              {savedPosters.length > 0 && (
                <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-white/20 text-[10px]">{savedPosters.length}</span>
              )}
            </button>
            {currentPosterId && (
              <button
                onClick={handleNewPoster}
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                data-testid="new-poster-btn"
                title="Start a new blank poster"
              >
                New
              </button>
            )}
          </div>

          {/* Zoom controls */}
          <div className="flex items-center gap-2">
            <button onClick={() => setZoom(z => Math.max(0.2, z - 0.1))} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" data-testid="zoom-out-btn">
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs text-slate-500 dark:text-slate-400 w-10 text-center font-mono">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => Math.min(1.5, z + 0.1))} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" data-testid="zoom-in-btn">
              <ZoomIn className="w-4 h-4" />
            </button>
            <button onClick={() => setZoom(0.55)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" data-testid="zoom-reset-btn">
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex max-w-[1800px] mx-auto" style={{ height: 'calc(100vh - 64px)' }}>
        {/* Left: Form Panel */}
        <div className="w-[380px] flex-shrink-0 overflow-y-auto border-r border-slate-200/50 dark:border-slate-800/50 bg-white dark:bg-slate-900">
          <PosterForm
            formData={formData}
            updateField={updateField}
            onGenerateBackground={handleGenerateBackground}
            isGeneratingBg={isGeneratingBg}
            bgError={bgError}
            onAddImage={addFreeFormImage}
            onAddText={addFreeFormText}
            freeFormElements={freeFormElements}
            selectedElementId={selectedElementId}
            onSelectElement={setSelectedElementId}
            onUpdateElement={updateElement}
            onRemoveElement={removeElement}
          />
        </div>

        {/* Right: Canvas + Export */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-auto flex items-center justify-center bg-slate-100 dark:bg-slate-950 p-4">
            <PosterCanvas
              ref={canvasRef}
              formData={formData}
              freeFormElements={freeFormElements}
              selectedElementId={selectedElementId}
              onSelectElement={setSelectedElementId}
              onUpdateElement={updateElement}
              zoom={zoom}
              format={currentFormat}
            />
          </div>
          <ExportControls
            canvasRef={canvasRef}
            formData={formData}
            format={currentFormat}
            freeFormElements={freeFormElements}
          />
        </div>
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="save-modal" onClick={() => setShowSaveModal(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800 dark:text-white">
                {currentPosterId ? 'Update Poster' : 'Save Poster'}
              </h3>
              <button onClick={() => setShowSaveModal(false)} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">Poster Name</label>
            <input
              type="text"
              value={posterName}
              onChange={e => setPosterName(e.target.value)}
              placeholder="e.g. LifeAdmin Grand Launch"
              className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              data-testid="poster-name-input"
              autoFocus
            />
            {saveError && (
              <p className="mt-2 text-xs text-red-500" data-testid="save-error">⚠️ {saveError}</p>
            )}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setShowSaveModal(false)}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePoster}
                disabled={isSaving}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r ${gradient} text-white hover:opacity-90 disabled:opacity-50`}
                data-testid="confirm-save-btn"
              >
                {isSaving ? 'Saving…' : (currentPosterId ? 'Update' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Modal — My Posters Gallery */}
      {showLoadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="load-modal" onClick={() => setShowLoadModal(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 w-full max-w-3xl mx-4 max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800 dark:text-white">
                My Saved Posters ({savedPosters.length})
              </h3>
              <button onClick={() => setShowLoadModal(false)} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            {savedPosters.length === 0 ? (
              <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No saved posters yet. Click <strong>Save</strong> to keep one for later editing.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {savedPosters.map(p => (
                  <div
                    key={p.id}
                    className="group relative bg-slate-50 dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500 transition-colors cursor-pointer shadow-sm hover:shadow-md"
                    data-testid={`saved-poster-${p.id}`}
                  >
                    {/* Preview area — aspect-[3/4] gives plenty of room; object-contain preserves the full poster (no cropping) and centers it with letterboxing if the source aspect differs. */}
                    <div
                      onClick={() => handleLoadPoster(p.id)}
                      className="aspect-[3/4] bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center overflow-hidden"
                    >
                      {p.thumbnail_data_url ? (
                        <img
                          src={p.thumbnail_data_url}
                          alt={p.name}
                          className="max-w-full max-h-full object-contain"
                          style={{ imageRendering: 'auto' }}
                        />
                      ) : (
                        <FolderOpen className="w-10 h-10 text-slate-400" />
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-semibold text-slate-800 dark:text-white truncate" title={p.name}>{p.name}</p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : ''}
                      </p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeletePoster(p.id); }}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500/95 text-white opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-opacity shadow-lg"
                      title="Delete"
                      data-testid={`delete-poster-${p.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-4 text-[11px] text-slate-400 dark:text-slate-500 text-center">
              💡 Tip: thumbnails captured before this update may look blurry — load any poster and re-save to refresh its preview.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
